import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { registerDevice } from "@/lib/device";
import { applyAppTheme } from "@/lib/theme";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState({});
  const [zakgeldActive, setZakgeldActive] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(localStorage.getItem("slackr_org") || null);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [pendingJoins, setPendingJoins] = useState(0);

  const loadPending = useCallback(async () => {
    try { const { data } = await api.get("/join-requests"); setPendingJoins(Array.isArray(data) ? data.length : 0); }
    catch { setPendingJoins(0); }
  }, []);

  const loadOrgs = useCallback(async () => {
    try {
      const { data } = await api.get("/my/organizations");
      setOrgs(data);
      const stored = localStorage.getItem("slackr_org");
      const valid = data.find((o) => o.org_id === stored);
      const next = valid ? stored : (data[0]?.org_id || null);
      if (next) { localStorage.setItem("slackr_org", next); setActiveOrg(next); }
      else { localStorage.removeItem("slackr_org"); setActiveOrg(null); }
    } catch { setOrgs([]); }
    finally { setOrgsLoaded(true); }
  }, []);

  const switchOrg = useCallback((org_id) => {
    localStorage.setItem("slackr_org", org_id);
    setActiveOrg(org_id);
    window.location.href = "/vandaag";
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setModules(data.module_visibility || {});
      setZakgeldActive(!!data.zakgeld_active);
      applyAppTheme({ accent: data.theme_accent, button: data.theme_button, font: data.theme_font });
    } catch {
      setModules({});
    } finally {
      setSettingsLoaded(true);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadOrgs();
      loadSettings();
      loadPending();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [loadSettings, loadOrgs, loadPending]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const persist = (data) => {
    if (data.session_token) localStorage.setItem("doenies_token", data.session_token);
    setUser(data.user);
    loadOrgs();
    loadSettings();
    loadPending();
    return data.user;
  };

  const login = async (email, password) => persist((await api.post("/auth/login", { email, password })).data);
  const register = async (email, password, name) => persist((await api.post("/auth/register", { email, password, name })).data);
  const pinLogin = async (user_id, pin) => persist((await api.post("/auth/pin-login", { user_id, pin })).data);
  const googleSession = async (session_id) => persist((await api.post("/auth/session", { session_id })).data);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("doenies_token");
    localStorage.removeItem("slackr_org");
    setUser(false); setOrgs([]); setActiveOrg(null);
    applyAppTheme({});
  };

  const moduleOn = useCallback((key) => modules[key] !== false, [modules]);

  // registreer dit apparaat zodra er een echte gebruiker is ingelogd
  useEffect(() => {
    if (user && user.user_id) registerDevice();
  }, [user]);

  const orgRole = orgs.find((o) => o.org_id === activeOrg)?.role || null;
  const effectiveUser = user ? { ...user, org_role: orgRole } : user;

  return (
    <AuthContext.Provider value={{ user: effectiveUser, loading, login, register, pinLogin, googleSession, logout, setUser, refresh: checkAuth, modules, moduleOn, zakgeldActive, settingsLoaded, refreshSettings: loadSettings, orgs, activeOrg, switchOrg, orgsLoaded, refreshOrgs: loadOrgs, pendingJoins, refreshPending: loadPending }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export const isAdmin = (user) => user && (user.role === "super_admin" || user.role === "beheerder" || user.org_role === "beheerder");
export const isSuperAdmin = (user) => user && user.role === "super_admin";
